import React, { useState, useEffect, useRef } from 'react';
import { Button, Empty, Checkbox, Space, Modal } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { ACTIVE_STATUS } from '../../constant/constant';
import { getQuestionBank } from '../../store/reducers/actionPermission';
import SurveyCategoryApi from '../../Api/SurveyCategoryApi';
import { toastError, toastSuccess, toastWarn } from '../Toast/ToastComponent';
import { QuestionBank } from '../../Api/QuestionBankApi';
import { DeletePopup } from '../ReusableComponents/DeletePopup';
import SearchInput from '../ReusableComponents/SearchInput';
import TmFilter from '../ReusableComponents/TmFilter';
import Loader from '../ReusableComponents/Loader';
import TmMasterTable from '../ReusableComponents/TmMasterTable';
import { questionTableColumns } from '../../constant/tableHeader';
import { useAuth } from "../../Context/AuthContext";

const QuestionBankComp = () => {
  const [categories, setCategories] = useState([]);
  const debounceTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const permission = useSelector(getQuestionBank);
  const { authState } = useAuth();
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterArr, setFilterArr] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') !== null
      ? (searchParams.get('status') === 'true' ? true : searchParams.get('status') === 'false' ? false : true)
      : true,
    category: searchParams.get('category') || undefined,
  });
  const [buttonPermission, setButtonPermissions] = useState({
    add: false,
    edit: false,
    delete: false,
    view: false,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
 const hasFetched = useRef(false);
  const filterList = React.useMemo(
    () => [
      {
        options: ACTIVE_STATUS,
        optionFilterProp: 'label',
        placeholder: 'Status',
        order: 1,
        col: 2,
        defaultValue: filterArr.status,
        controlName: 'status',
        allowClear: true,
        input: 'select',
      },
      {
        options: categories,
        placeholder: 'Select Category',
        controlName: 'category',
        input: 'select',
        allowClear: true,
        size: 'small',
        style: { width: 250, float: 'right' },
        col: 3,
        loading: isCategoriesLoading,
        defaultValue: filterArr.category || undefined,
      },
      {
        order: 1,
        col: 'auto',
        className: 'ms-auto pe-0',
        input: 'button',
        type: 'dashed',
        htmlType: 'button',
        text: 'Reset',
      },
      { col: 5 },
    ],
    [categories, isCategoriesLoading, filterArr.status, filterArr.category],
  );

  useEffect(() => {
    if (permission) {
      if (!permission?.checked) return navigate('/');
      const obj = {};
      permission.actions.forEach((el) => {
        obj[el.statename.toLowerCase()] = el.checked;
      });
      setButtonPermissions(obj);
    }
  }, [permission, navigate]);

  const formatData = (list, currentCategories = []) => {
    if (!Array.isArray(list)) return [];

    return list.map((item, index) => {
      const categoryMatch = currentCategories.find(
        (cat) => Number(cat.value) === Number(item.category_id),
      );
      const categoryName = categoryMatch ? categoryMatch.label : '-';

      let optionDisplay = '-';
      try {
        const type = (item.question_type || '').toLowerCase();
        const opts =
          typeof item.option === 'string'
            ? JSON.parse(item.option)
            : item.option;

        if (type === 'matrix') {
          const rows = opts?.rows || [];
          const cols = opts?.columns || [];
          optionDisplay = `${rows.length} Rows x ${cols.length} Cols`;
        } else if (type === 'rating') {
          const rateValue = Array.isArray(opts)
            ? opts[0]?.title
            : opts?.title || 5;
          optionDisplay = `Scale: 1-${rateValue}`;
        } else if (type === 'slider' || type === 'sliders') {
          const sliderSettings = Array.isArray(opts) ? opts[0] : opts;
          const minVal = sliderSettings?.min ?? 0;
          const maxVal = sliderSettings?.max ?? 100;
          optionDisplay = `Range: ${minVal} - ${maxVal}`;
        } else if (Array.isArray(opts)) {
          optionDisplay = `${opts.length} Options`;
        }
      } catch (e) {
        optionDisplay = '-';
      }

      return {
        ...item,
        key: String(item.id || index),
        serialNo: index + 1,
        status: item.activeStatus,
       createdAt: item.createdAt,
        type: item.question_type || '-',
        categoryName: categoryName,
        optionRange: optionDisplay,
      };
    });
  };

  const fetchCategories = async () => {
    setIsCategoriesLoading(true);
    try {
      const res = await SurveyCategoryApi.getDropdown();
      if (res?.data?.success) {
        const mapped = res.data.result.map((cat) => ({
          label: cat.categoryName,
          value: String(cat.id),
        }));
        setCategories(mapped);
        return mapped;
      }
      return [];
    } catch (err) {
      toastError('Failed to load categories');
      return [];
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  const getApilist = async (
    filters = {},
    freshCats = null,
    page = pagination,
  ) => {
    try {
      setLoading(true);
      setSelectedRowKeys([]);

      // Get fresh categories if not provided
      let currentCategories = freshCats;
      if (!currentCategories || currentCategories.length === 0) {
        currentCategories = await fetchCategories();
      }
      
      // Ensure categories state is updated
      if (currentCategories && currentCategories.length > 0 && categories.length === 0) {
        setCategories(currentCategories);
      }

      const payload = {
        category_id: filters?.category ,
        status: filters?.status !== undefined ? filters.status : true,
        is_required: '',
        search: filters?.search || '',
        limit: 5000,
        offset: 0,
      };

      const res = await QuestionBank.getQuestionList(payload);
      if (res?.data?.success) {
        const apiRows = res.data.result.rows || [];
        const mapped = formatData(apiRows, currentCategories);
        setData(mapped);
        setOriginalData(mapped);
      } else {
        setData([]);
        setOriginalData([]);
      }
    } catch (error) {
      console.error('API Error:', error);
      toastError('Failed to fetch data');
      setData([]);
      setOriginalData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load — read filters from URL params
  useEffect(() => {
    if (hasFetched.current) return;

  hasFetched.current = true;
    const init = async () => {
      if (!permission?.checked) return;
      
      setLoading(true);
      try {
        const freshCategories = await fetchCategories();
        await getApilist(
          { 
            search: filterArr.search, 
            status: filterArr.status, 
            category: filterArr.category,
          },
          freshCategories,
        );
      } catch (error) {
        console.error('Initialization error:', error);
        toastError('Failed to load initial data');
      } finally {
        setLoading(false);
      }
    };
    
    init();
  }, [permission]);

  const onSelectChange = (id) => {
    const stringId = String(id);
    setSelectedRowKeys((prev) =>
      prev.includes(stringId)
        ? prev.filter((key) => key !== stringId)
        : [...prev, stringId],
    );
  };

  const onSelectAll = (e) => {
    setSelectedRowKeys(
      e.target.checked ? data.map((item) => String(item.id)) : [],
    );
  };

  const handleFilterChange = async (filters) => {
    const updatedFilters = { ...filterArr, ...filters };
    setFilterArr(updatedFilters);

    // Write filters to URL so they survive navigation
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (updatedFilters.status === null || updatedFilters.status === undefined || updatedFilters.status === '') {
        next.delete('status');
      } else {
        next.set('status', String(updatedFilters.status));
      }
      if (updatedFilters.category) {
        next.set('category', String(updatedFilters.category));
      } else {
        next.delete('category');
      }
      if (updatedFilters.search) {
        next.set('search', updatedFilters.search);
      } else {
        next.delete('search');
      }
      return next;
    });

    const freshCategories = await fetchCategories();
    await getApilist(updatedFilters, freshCategories);
  };

  const handleSearchChange = (value) => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      handleFilterChange({ search: value });
    }, 400);
  };

  const toggleModal = (type, record) => {
    if (!record) return;
    const cleanRecord = { ...record };
    delete cleanRecord.selection;
    const catId = cleanRecord.category_id || filterArr.category || 0;

    if (type === 'View') {
      navigate(`/view-question/${cleanRecord.id}/${catId}`, {
        state: { oldData: cleanRecord, isView: true },
      });
    } else if (type === 'Edit') {
      navigate(`/add-question/${cleanRecord.id}/${catId}`, {
        state: { oldData: cleanRecord, isView: false },
      });
    }
  };

  const handleEdit = (record) => toggleModal('Edit', record);

  const callDelete = async (id) => {
    const idsToDelete = Array.isArray(id) ? id : [id];
    await DeletePopup(async (ok) => {
      if (!ok) return;
      try {
        const res = await QuestionBank.deleteQuestion(idsToDelete);
        if (res.data.success) {
          toastSuccess(res.data.message);
          // Refresh both categories and questions after delete
          const freshCategories = await fetchCategories();
          await getApilist(filterArr, freshCategories);
        }
      } catch (err) {
        toastError('Something went wrong');
      }
    });
  };

  const handleStatusToggle = async (row) => {
    const isCurrentlyActive =
      row.status === true ||
      row.status === 1 ||
      String(row.status).toLowerCase() === 'active';

    const newStatus = isCurrentlyActive ? 'inactive' : 'active';

    Modal.confirm({
      title: `Are you sure you want to ${newStatus === 'inactive' ? 'Deactivate' : 'Activate'} this question?`,
      onOk: async () => {
        try {
          const payload = {
            id: row.id,
            status: newStatus === 'active',
          };

          const res = await QuestionBank.questionStatus(payload);

          if (res.data?.success) {
            toastSuccess(
              `Question ${newStatus === 'inactive' ? 'Deactivated' : 'Activated'} successfully`
            );
            // Refresh data after status change
            const freshCategories = await fetchCategories();
            await getApilist(filterArr, freshCategories);
          } else {
            toastWarn(res.data?.message);
          }
        } catch (err) {
          toastError(err?.message || "Something went wrong");
        }
      }
    });
  };

  const handleReset = async () => {
    const resetFilters = { search: '', status: true, category: undefined };
    setFilterArr(resetFilters);
    // Clear URL params on reset
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('status');
      next.delete('category');
      next.delete('search');
      return next;
    });
    const freshCategories = await fetchCategories();
    await getApilist(resetFilters, freshCategories);
  };

  return (
    <div >
      <div className="d-md-flex align-items-center mb-3">
        <h5 className="mb-0">Question Bank</h5>
        <div className="ms-auto d-flex align-items-center">
          <Space>
            <SearchInput
              placeholder="Search..."
              value={filterArr.search}
              onSearchChange={handleSearchChange}
            />
            {selectedRowKeys.length > 0 && buttonPermission.delete && (
              <Button
                danger
                ghost
                icon={<DeleteOutlined />}
                onClick={() => callDelete(selectedRowKeys)}
              >
                Delete ({selectedRowKeys.length})
              </Button>
            )}
            {buttonPermission.add && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/add-question')}
              />
            )}
          </Space>
        </div>
      </div>

      <div>
        <TmFilter
          allowClear
          filterArray={filterList}
          handleFilterChange={handleFilterChange}
          onReset={handleReset}
        />
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="mt-2">
          {data.length > 0 ? (
            <TmMasterTable
              buttonPermission={buttonPermission}
              rows={data.map((item) => ({
                ...item,
                selection: (
                  <div
                    style={{ textAlign: 'center' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedRowKeys.includes(String(item.id))}
                      onChange={() => onSelectChange(item.id)}
                    />
                  </div>
                ),
              }))}
              columns={[
                {
                  headerName: (
                    <div style={{ textAlign: 'center', width: '100%' }}>
                      <Checkbox
                        onChange={onSelectAll}
                        indeterminate={
                          selectedRowKeys.length > 0 &&
                          selectedRowKeys.length < data.length
                        }
                        checked={
                          data.length > 0 &&
                          selectedRowKeys.length === data.length
                        }
                      />
                    </div>
                  ),
                  field: 'selection',
                  width: 60,
                },
                ...questionTableColumns,
              ]}
              custom={['action']}
              handleDelete={callDelete}
              handleEdit={handleEdit}
              toggleModal={toggleModal}
              handleStatus={handleStatusToggle}
            />
          ) : (
            <Empty description="No questions found." />
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionBankComp;